// =============================================================================
// COMMISSIONSTATE.JS — Where an agent's commission stands, deal by deal.
// =============================================================================
// Lifted out of the Commission Payments panel so the dashboard can show an
// agent the same numbers without keeping a second copy of the rules that would
// drift away from it. Every state an agent's commission can be in:
//
//   waiting  — the client hasn't paid the deal yet
//   payable  — the client paid; due on a paycheck within PAYOUT_WINDOW_DAYS
//   paid     — on a paycheck (a payout_items row links the deal to a payout)
//   forfeit  — the deal's month missed the agent's monthly qualifier, so the
//              whole month's commission is gone (see MONTHLY_QUALIFIER)
//
// Forfeited commission is deliberately left out of `earned`: it is money the
// agent never gets, and counting it would promise a payout that won't come.
// =============================================================================
import { isPaidStatus } from './constants'
import { dealPeriod } from './period'
import { MONTHLY_QUALIFIER } from './commissions'

// Commission is due on a paycheck within 30 days of the client paying the deal.
export const PAYOUT_WINDOW_DAYS = 30

/** The deal column holding this agent's cut: 'Mirelle' -> 'commission_mirelle'. */
export const commissionField = (agentName) => `commission_${String(agentName || '').toLowerCase()}`

/**
 * Booked sales per 'YYYY-MM' for the deals this agent closed.
 *
 * ALL of their booked deals count toward the monthly qualifier, including
 * sub-$1K ones that pay no commission themselves. Keyed by period and never by
 * the bare month name, which carries no year — two separate $10K Januarys
 * summing to $20K once wrongly cleared Lydie's qualifier.
 */
export function monthlySalesByPeriod(deals, agentName) {
  const sales = new Map()
  for (const d of deals || []) {
    if ((d.agent || '') !== agentName) continue
    const key = dealPeriod(d) || 'Undated'
    sales.set(key, (sales.get(key) || 0) + (d.deal_amount || 0))
  }
  return sales
}

/**
 * Everything the UI needs about one agent's commission position.
 *
 * `now` is injectable so tests can sit on either side of the 30-day window.
 * Returns { field, qualifier, monthlySales, monthQualifies, itemByDeal,
 * agentDeals, dealState, totals, paychecks }.
 */
export function createCommissionView({ deals = [], agentName, payouts = [], payoutItems = [], now = new Date() } = {}) {
  const field = commissionField(agentName)
  const qualifier = MONTHLY_QUALIFIER[String(agentName || '').toLowerCase()]
  const monthlySales = monthlySalesByPeriod(deals, agentName)

  const monthQualifies = (period) =>
    !qualifier || (monthlySales.get(period || 'Undated') || 0) >= qualifier

  // Paycheck linkage: deal id -> payout item, with its payout row attached.
  const payoutById = new Map((payouts || []).map(p => [p.id, p]))
  const itemByDeal = new Map()
  for (const it of payoutItems || []) {
    if (it.agent_name === agentName) itemByDeal.set(it.deal_id, { ...it, payout: payoutById.get(it.payout_id) })
  }

  // Only deals that actually pay this agent something, biggest first.
  const agentDeals = (deals || [])
    .filter(d => (d[field] || 0) > 0)
    .sort((a, b) => (b.deal_amount || 0) - (a.deal_amount || 0))

  const dealState = (deal) => {
    const item = itemByDeal.get(deal.id)
    if (item) return { state: 'paid', item }
    if (!monthQualifies(dealPeriod(deal))) return { state: 'forfeit' }
    if (isPaidStatus(deal.status)) {
      const paidAt = deal.paid_at || deal.status_updated_at
      const due = paidAt ? new Date(new Date(paidAt).getTime() + PAYOUT_WINDOW_DAYS * 86400000) : null
      return { state: 'payable', due, overdue: due ? due < now : false }
    }
    return { state: 'waiting' }
  }

  const totals = { earned: 0, paidOut: 0, payable: 0, overdue: 0, waiting: 0, forfeited: 0, revenue: 0 }
  for (const d of agentDeals) {
    totals.revenue += d.deal_amount || 0
    const cut = d[field] || 0
    const s = dealState(d)
    if (s.state === 'forfeit') { totals.forfeited += cut; continue }
    totals.earned += cut
    if (s.state === 'paid') totals.paidOut += cut
    else if (s.state === 'payable') {
      totals.payable += cut
      if (s.overdue) totals.overdue += cut
    } else totals.waiting += cut
  }

  // This agent's paychecks, most recently paid first, each carrying the deals
  // it covers — the answer to "what was this payment for?". An ad-hoc payout
  // has no linked deals, so `lines` is empty and `amount` stands on its own.
  const dealById = new Map((deals || []).map(d => [d.id, d]))
  const linesByPayout = new Map()
  for (const it of payoutItems || []) {
    if (it.agent_name !== agentName) continue
    const line = { deal_id: it.deal_id, amount: Number(it.amount) || 0, deal: dealById.get(it.deal_id) || null }
    const lines = linesByPayout.get(it.payout_id)
    if (lines) lines.push(line); else linesByPayout.set(it.payout_id, [line])
  }
  const paychecks = (payouts || [])
    .filter(p => p.agent_name === agentName)
    .map(p => {
      const lines = (linesByPayout.get(p.id) || []).sort((a, b) => b.amount - a.amount)
      return { ...p, lines, dealCount: lines.length }
    })
    .sort((a, b) => String(b.paid_at || '').localeCompare(String(a.paid_at || '')))

  return { agentName, field, qualifier, monthlySales, monthQualifies, itemByDeal, agentDeals, dealState, totals, paychecks }
}
